import { useState, useEffect, useMemo } from "react";
import { Listbox } from "@headlessui/react";
import { ChevronDown, Search } from "lucide-react";
import {
  getCountries,
  getCountryCallingCode,
  CountryCode,
  isValidPhoneNumber,
  parsePhoneNumberWithError,
} from "libphonenumber-js";

interface Country {
  code: CountryCode;
  dialCode: string;
  name: string;
}

// Get all countries and their metadata
const countries: Country[] = getCountries()
  .map((code) => ({
    code,
    dialCode: `+${getCountryCallingCode(code)}`,
    name: new Intl.DisplayNames(["en"], { type: "region" }).of(code) || code,
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

// Function to convert country code to flag emoji
const getFlagEmoji = (countryCode: CountryCode) => {
  const codePoints = countryCode
    .toUpperCase()
    .split("")
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  placeholder?: string;
  className?: string;
  defaultCountry?: string;
}

export function PhoneInput({
  value,
  onChange,
  error,
  placeholder = "Enter phone number",
  className = "",
  defaultCountry,
}: PhoneInputProps) {
  const [selectedCountry, setSelectedCountry] = useState<Country>(() => {
    if (defaultCountry) {
      const country = countries.find((c) => c.name === defaultCountry);
      if (country) return country;
    }
    return countries[0];
  });
  const [localNumber, setLocalNumber] = useState("");
  const [validationError, setValidationError] = useState<string | undefined>(
    error
  );
  const [searchQuery, setSearchQuery] = useState("");

  const filteredCountries = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return countries.filter(
      (country) =>
        country.name.toLowerCase().includes(query) ||
        country.dialCode.includes(query) ||
        country.code.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  useEffect(() => {
    // Initialize with existing value if provided
    if (value) {
      try {
        const phoneNumber = parsePhoneNumberWithError(value);
        if (phoneNumber) {
          const country = countries.find((c) => c.code === phoneNumber.country);
          if (country) {
            setSelectedCountry(country);
            setLocalNumber(phoneNumber.nationalNumber);
          }
        }
      } catch (e) {
        // Invalid number, ignore
      }
    }
  }, [value]);

  useEffect(() => {
    setValidationError(error);
  }, [error]);

  const handleCountryChange = (country: Country) => {
    setSelectedCountry(country);
    const newValue = country.dialCode + localNumber;
    validateAndUpdate(newValue);
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newNumber = e.target.value.replace(/\D/g, "");
    setLocalNumber(newNumber);
    const newValue = selectedCountry.dialCode + newNumber;
    validateAndUpdate(newValue);
  };

  const validateAndUpdate = (newValue: string) => {
    try {
      if (newValue === selectedCountry.dialCode) {
        // Just the country code, no validation needed
        setValidationError(undefined);
        onChange(newValue);
        return;
      }

      const isValid = isValidPhoneNumber(newValue);
      if (isValid) {
        setValidationError(undefined);
      } else {
        setValidationError("Please enter a valid phone number");
      }
      onChange(newValue);
    } catch (e) {
      setValidationError("Please enter a valid phone number");
      onChange(newValue);
    }
  };

  const formatNumber = (number: string) => {
    try {
      if (!number) return "";
      const phoneNumber = parsePhoneNumberWithError(
        selectedCountry.dialCode + number
      );
      return phoneNumber.formatNational();
    } catch (e) {
      return number;
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div className="flex rounded-lg shadow-sm">
        <Listbox value={selectedCountry} onChange={handleCountryChange}>
          <div className="relative">
            <Listbox.Button className="relative w-32 cursor-default rounded-l-lg border border-r-0 border-gray-300 bg-white py-2.5 pl-3 pr-10 text-left text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500">
              <div className="flex items-center">
                <span className="mr-2">
                  {getFlagEmoji(selectedCountry.code)}
                </span>
                <span className="block truncate">
                  {selectedCountry.dialCode}
                </span>
              </div>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronDown
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </span>
            </Listbox.Button>
            <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-80 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              <div className="sticky top-0 bg-white px-3 py-2 border-b">
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
                    <Search
                      className="h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </div>
                  <input
                    type="text"
                    className="block w-full rounded-md border-0 py-1.5 pl-8 pr-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6"
                    placeholder="Search countries..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
              {filteredCountries.map((country) => (
                <Listbox.Option
                  key={country.code}
                  value={country}
                  className={({ active }: { active: boolean }) =>
                    `relative cursor-default select-none py-2 pl-3 pr-9 ${
                      active ? "bg-blue-100 text-blue-900" : "text-gray-900"
                    }`
                  }
                >
                  {({ selected }: { selected: boolean }) => (
                    <div className="flex items-center">
                      <span className="mr-2">{getFlagEmoji(country.code)}</span>
                      <span
                        className={`block truncate ${
                          selected ? "font-medium" : "font-normal"
                        }`}
                      >
                        {country.name} ({country.dialCode})
                      </span>
                    </div>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </div>
        </Listbox>
        <input
          type="tel"
          value={formatNumber(localNumber)}
          onChange={handleNumberChange}
          className="block w-full rounded-r-lg border border-gray-300 py-2.5 px-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          placeholder={placeholder}
        />
      </div>
      {validationError && (
        <p className="mt-1 text-sm text-red-600">{validationError}</p>
      )}
    </div>
  );
}
